import { type ReactNode } from 'react'

/**
 * The pieces every marketing page is built from.
 *
 * <p>Shared rather than page-local: the site is a set of pages now, and a
 * section heading that drifted between them would read as two products. One
 * definition here means the whole site moves together.
 */

/* ---------------------------------------------------------------- status */

export type Status = 'today' | 'building' | 'planned'

const STATUS_LABEL: Record<Status, string> = {
  today: 'Available today',
  building: 'In development',
  planned: 'Planned',
}

const STATUS_CLASS: Record<Status, string> = {
  today: 'bg-brand-tint text-brand-tint-ink',
  building: 'bg-[#fdf2e3] text-[#96590c]',
  planned: 'bg-brand-panel text-brand-muted border border-brand-line',
}

/** Says where a capability really stands, wherever one is mentioned. */
export function StatusChip({ status }: { status: Status }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-lg px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  )
}

/* --------------------------------------------------------------- section */

/** One titled band of a marketing page, set the way the suite sets them. */
export function VisionSection({
  id,
  eyebrow,
  title,
  lede,
  tone = 'page',
  status,
  children,
}: {
  id?: string
  eyebrow: string
  title: string
  lede?: string
  tone?: 'page' | 'panel'
  status?: Status
  children: ReactNode
}) {
  return (
    <section
      id={id}
      className={`scroll-mt-16 border-b border-brand-line ${
        tone === 'panel' ? 'bg-brand-panel' : 'bg-brand-page'
      }`}
    >
      <div className="nv-container pb-16 pt-12 sm:pb-20 sm:pt-14">
        <div className="flex flex-wrap items-center gap-3">
          <span className="nv-eyebrow">{eyebrow}</span>
          {status && <StatusChip status={status} />}
        </div>
        <h2 className="nv-h2 mt-3 max-w-3xl text-[1.8rem] leading-tight sm:text-[2.2rem]">
          {title}
        </h2>
        {lede && (
          <p className="mt-3 max-w-2xl text-[15.5px] leading-relaxed text-brand-body">{lede}</p>
        )}
        <div className="mt-9">{children}</div>
      </div>
    </section>
  )
}

/**
 * The short band that closes a subpage, pointing at the download.
 *
 * <p>Every page below the home page ends on one, so a visitor who arrived on
 * a deep link is never left without the next step.
 */
export function PageOutro({ children }: { children: ReactNode }) {
  return (
    <section className="border-b border-brand-line bg-[linear-gradient(160deg,#ecfdf5,#ffffff)] dark:bg-[linear-gradient(160deg,#0e1817,#152220)]">
      <div className="nv-container py-14 text-center sm:py-16">{children}</div>
    </section>
  )
}
